import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import bridgeService from '../../../services/bridgeService';
import authService from '../../auth/services/authService';
import companyService from '../../companies/services/companyService';
import Modal from '../../../components/common/Modal';
import ConfirmModal from '../../../components/common/ConfirmModal';
import Loading from '../../../components/common/Loading';
import LoadingButton from '../../../components/common/LoadingButton';
import useNotify from '../../../hooks/useNotify';
import BridgeStaffForm from '../../../components/bridge/BridgeStaffForm';
import BridgeSensorsReadonly from '../../../components/bridge/BridgeSensorsReadonly';
import BridgeMapPanel, { getBridgeCoordinates, prefetchBridgeLatestStatuses } from '../../../components/bridge/BridgeMapPanel';
import './BridgeListPage.css';
import {
  applyBridgeCountryToStaff,
  applyCompanyDialToEmptyStaff,
  applyLockedFirstAdmin,
  emptyStaffForBridge,
  filterPeople,
  filterManagers,
  getDialCodeForBridgeCountry,
  personFromCompanyAdmin,
  personFromLoggedInUser,
  staffFromBridge,
  validateBridgeStaff,
} from '../../../utils/bridgeStaff';
import AppMultiSelect from '../../../components/common/AppMultiSelect';
import { COUNTRIES, getStatesForCountry } from '../../../utils/countryStates';
import { filterBridgesByRole, filterBridgesForMap } from '../../../utils/bridgeAccess';

const emptyForm = {
  name: '',
  country: 'USA',
  states: [],
  city: '',
  division: '',
  lat: '',
  lng: '',
  zipCode: '',
};

const parseStates = (value) => {
  if (Array.isArray(value)) return value.map((s) => String(s).trim()).filter(Boolean);
  return String(value || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
};

const formatStates = (states) => parseStates(states).join(', ');

const validateCoordinateInputs = (form) => {
  const latEntered = String(form.lat).trim() !== '';
  const lngEntered = String(form.lng).trim() !== '';
  if (!latEntered && !lngEntered) return null;

  const lat = parseFloat(form.lat);
  const lng = parseFloat(form.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return 'Latitude and Longitude must both be valid numbers.';
  }
  if (lat < -90 || lat > 90) {
    return 'Latitude must be between -90 and 90.';
  }
  if (lng < -180 || lng > 180) {
    return 'Longitude must be between -180 and 180.';
  }
  return null;
};

const bridgePayload = (form) => {
  const lat = parseFloat(form.lat);
  const lng = parseFloat(form.lng);
  const coordinates =
    Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : { lat: null, lng: null };

  return {
    name: form.name.trim(),
    country: form.country,
    state: formatStates(form.states),
    city: form.city,
    division: form.division,
    coordinates,
    zipCode: form.zipCode,
  };
};

const resolveBridge = (res) => res?.bridge ?? res;

const mergeBridgeListSources = (listBridges, mapBridges) => {
  const merged = new Map();

  const add = (bridge) => {
    if (!bridge?._id) return;
    const key = String(bridge._id);
    const existing = merged.get(key);
    if (!existing) {
      merged.set(key, { ...bridge });
      return;
    }
    merged.set(key, {
      ...existing,
      ...bridge,
      createdBy: bridge.createdBy ?? existing.createdBy,
      admins: bridge.admins?.length ? bridge.admins : existing.admins,
      owners: bridge.owners?.length ? bridge.owners : existing.owners,
      managers: bridge.managers?.length ? bridge.managers : existing.managers,
      sensors: bridge.sensors?.length ? bridge.sensors : existing.sensors,
      dataLoggers: bridge.dataLoggers?.length ? bridge.dataLoggers : existing.dataLoggers,
    });
  };

  (mapBridges || []).forEach(add);
  (listBridges || []).forEach(add);
  return Array.from(merged.values());
};

const BRIDGE_LIST_PAGE_SIZE = 10;

export default function BridgeListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bridges, setBridges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(null);
  const [modalStep, setModalStep] = useState(1);
  const [bridgeId, setBridgeId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [staff, setStaff] = useState(() => emptyStaffForBridge('USA', user));
  const [companyCountry, setCompanyCountry] = useState('');
  const [sensors, setSensors] = useState([]);
  const [dataLoggers, setDataLoggers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [mapBridge, setMapBridge] = useState(null);
  const [blinkingRowId, setBlinkingRowId] = useState(null);
  const [pulseBridgeId, setPulseBridgeId] = useState(null);
  const [mapPulseToken, setMapPulseToken] = useState(0);
  const [listView, setListView] = useState('map');
  const mapSensorHydrateIdsRef = useRef(new Set());
  const { showToast } = useNotify();

  const canAdd = ['super_admin', 'bridge_admin'].includes(user?.role);
  const canEditDelete = canAdd && user?.role !== 'super_admin';

  const loadBridges = useCallback(async (options = {}) => {
    const { silent = false } = options;
    if (!silent) setLoading(true);
    try {
      const listPromise = bridgeService.getAll();
      const mapPromise = bridgeService.getAllForMap();
      const prefetchLatest = (payload) => {
        const rows = payload?.bridges || [];
        prefetchBridgeLatestStatuses(
          rows.filter((bridge) => getBridgeCoordinates(bridge)).map((bridge) => bridge._id)
        );
      };
      listPromise.then(prefetchLatest).catch(() => {});
      mapPromise.then(prefetchLatest).catch(() => {});
      const [listData, mapData] = await Promise.all([
        listPromise,
        mapPromise,
      ]);
      const listBridges = listData.bridges || [];
      const mapBridgeList = mapData.bridges || [];
      setBridges(mergeBridgeListSources(listBridges, mapBridgeList));
      mapSensorHydrateIdsRef.current.clear();
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadBridges();
  }, [loadBridges]);

  // Map pins render immediately. Only fetch detail when list/map payload omitted
  // the sensors field entirely (empty [] already means "no sensors" — no extra call).
  useEffect(() => {
    if (loading || !bridges.length) return undefined;
    let cancelled = false;

    const pending = bridges.filter((bridge) => {
      const id = String(bridge._id || '');
      if (!id || mapSensorHydrateIdsRef.current.has(id)) return false;
      if (!getBridgeCoordinates(bridge)) return false;
      // getAll/getAllForMap already return full docs; skip when sensors is present.
      return !Array.isArray(bridge.sensors);
    });
    if (!pending.length) return undefined;

    pending.forEach((bridge) => mapSensorHydrateIdsRef.current.add(String(bridge._id)));

    const HYDRATE_CONCURRENCY = 3;

    (async () => {
      let nextIndex = 0;
      const workerCount = Math.min(HYDRATE_CONCURRENCY, pending.length);
      await Promise.all(
        Array.from({ length: workerCount }, async () => {
          while (!cancelled) {
            const index = nextIndex;
            nextIndex += 1;
            if (index >= pending.length) return;
            const bridge = pending[index];
            try {
              const detail = await bridgeService.fetchDetail(bridge._id);
              if (cancelled) return;
              if (!detail) continue;
              setBridges((prev) => prev.map((item) => {
                if (String(item._id) !== String(bridge._id)) return item;
                return {
                  ...item,
                  ...detail,
                  coordinates: detail.coordinates || item.coordinates,
                  sensors: Array.isArray(detail.sensors) ? detail.sensors : item.sensors,
                  dataLoggers: Array.isArray(detail.dataLoggers) ? detail.dataLoggers : item.dataLoggers,
                };
              }));
            } catch {
              mapSensorHydrateIdsRef.current.delete(String(bridge._id));
            }
          }
        })
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [loading, bridges]);

  useEffect(() => {
    if (!pulseBridgeId) return undefined;
    const timer = window.setTimeout(() => setPulseBridgeId(null), 2200);
    return () => window.clearTimeout(timer);
  }, [pulseBridgeId, mapPulseToken]);

  const resolveLockedAdminPerson = useCallback(async ({ bridge, country } = {}) => {
    const fallbackDial = getDialCodeForBridgeCountry(country);

    if (user?.role === 'bridge_admin') {
      try {
        const data = await authService.getMe();
        const companyCountry = data?.companyAdmin?.companyCountry || '';
        const fromCompany = personFromCompanyAdmin(
          data?.companyAdmin,
          getDialCodeForBridgeCountry(companyCountry) || fallbackDial,
        );
        if (fromCompany) return { person: fromCompany, companyCountry };
        return {
          person: personFromLoggedInUser(data?.user || user, fallbackDial, bridges),
          companyCountry,
        };
      } catch {
        return { person: personFromLoggedInUser(user, fallbackDial, bridges), companyCountry: '' };
      }
    }

    if (user?.role === 'super_admin') {
      const companyId = bridge?.company?._id || bridge?.company || user?.company?._id || user?.company;
      if (!companyId) return { person: null, companyCountry: '' };
      try {
        const data = await companyService.getOne(companyId);
        const company = data?.company || data;
        return {
          person: personFromCompanyAdmin(
            company?.bridgeAdmins?.[0],
            getDialCodeForBridgeCountry(company?.country) || fallbackDial,
          ),
          companyCountry: company?.country || '',
        };
      } catch {
        return { person: null, companyCountry: '' };
      }
    }

    return { person: null, companyCountry: '' };
  }, [user, bridges]);

  const staffWithLockedAdmin = useCallback(async (staff, { bridge, country } = {}) => {
    const { person, companyCountry } = await resolveLockedAdminPerson({ bridge, country });
    const withCompanyDial = companyCountry
      ? applyCompanyDialToEmptyStaff(staff, companyCountry)
      : staff;
    return {
      staff: person ? applyLockedFirstAdmin(withCompanyDial, person) : withCompanyDial,
      companyCountry,
    };
  }, [resolveLockedAdminPerson]);

  const closeModal = () => {
    setModal(null);
    setModalStep(1);
    setBridgeId(null);
    setForm(emptyForm);
    setCompanyCountry('');
    setStaff(emptyStaffForBridge('USA', user, bridges));
    setSensors([]);
    setDataLoggers([]);
  };

  const openAdd = async () => {
    setForm(emptyForm);
    const loaded = await staffWithLockedAdmin(
      emptyStaffForBridge(emptyForm.country, user, bridges),
      { country: emptyForm.country },
    );
    setCompanyCountry(loaded.companyCountry || '');
    setStaff(loaded.staff);
    setModalStep(1);
    setBridgeId(null);
    setSensors([]);
    setDataLoggers([]);
    setModal('add');
  };

  const onCountryChange = (country) => {
    setForm((p) => ({ ...p, country, states: [] }));
    setStaff((prev) => applyBridgeCountryToStaff(prev, country));
  };

  const stateOptions = getStatesForCountry(form.country);

  const openEdit = async (b) => {
    setForm({
      name: b.name,
      country: b.country,
      states: parseStates(b.state),
      city: b.city,
      division: b.division,
      lat: b.coordinates?.lat ?? '',
      lng: b.coordinates?.lng ?? '',
      zipCode: b.zipCode,
    });
    setModalStep(1);
    setBridgeId(b._id);
    setModal(b);
    setSensors(b.sensors || []);
    setDataLoggers(b.dataLoggers || []);
    try {
      const res = await bridgeService.getOne(b._id);
      const bridge = resolveBridge(res);
      const loaded = await staffWithLockedAdmin(staffFromBridge(bridge), {
        bridge,
        country: bridge.country,
      });
      setCompanyCountry(loaded.companyCountry || '');
      setStaff(loaded.staff);
      setSensors(bridge.sensors || []);
      setDataLoggers(bridge.dataLoggers || []);
    } catch {
      const loaded = await staffWithLockedAdmin(staffFromBridge(b), {
        bridge: b,
        country: b.country,
      });
      setCompanyCountry(loaded.companyCountry || '');
      setStaff(loaded.staff);
    }
  };

  const saveBasicBridge = async () => {
    if (!form.name.trim()) {
      showToast('Bridge Name is required.', 'error');
      return false;
    }
    const coordErr = validateCoordinateInputs(form);
    if (coordErr) {
      showToast(coordErr, 'error');
      return false;
    }
    setSaving(true);
    try {
      let id = bridgeId;
      let savedBridge = null;
      const payload = bridgePayload(form);

      if (modal === 'add' && !id) {
        const res = await bridgeService.create({
          ...payload,
          owners: [],
          admins: [],
          managers: [],
        });
        const created = resolveBridge(res);
        id = created._id;
        savedBridge = created;
        setBridgeId(id);
      } else {
        await bridgeService.update(id, payload);
        savedBridge = bridges.find((item) => String(item._id) === String(id)) || { _id: id };
      }

      showToast('Bridge details saved. Add users below.');
      const { person, companyCountry: savedCompanyCountry } = await resolveLockedAdminPerson({
        bridge: savedBridge,
        country: form.country,
      });
      if (savedCompanyCountry) setCompanyCountry(savedCompanyCountry);
      setStaff((prev) => {
        let next = applyBridgeCountryToStaff(prev, form.country);
        if (savedCompanyCountry) next = applyCompanyDialToEmptyStaff(next, savedCompanyCountry);
        return person ? applyLockedFirstAdmin(next, person) : next;
      });
      setModalStep(2);
      await loadBridges({ silent: true });
      return true;
    } catch (e) {
      showToast(e.message, 'error');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveStaff = async (closeAfter) => {
    const err = validateBridgeStaff(staff.owners, staff.admins, staff.managers);
    if (err) {
      showToast(err, 'error');
      return;
    }

    const id = bridgeId || modal?._id;
    if (!id) {
      showToast('Save bridge details first.', 'error');
      return;
    }

    setSaving(true);
    try {
      await bridgeService.update(id, {
        ...bridgePayload(form),
        owners: filterPeople(staff.owners),
        admins: filterPeople(staff.admins),
        managers: filterManagers(staff.managers),
      });
      showToast(closeAfter ? 'Bridge and users saved.' : 'Bridge users saved.');
      if (closeAfter) {
        closeModal();
        loadBridges();
      }
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleStep1Save = () => saveBasicBridge();
  const handleStep2SaveClose = () => saveStaff(true);

  const confirmDeleteBridge = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await bridgeService.delete(deleteTarget.id);
      showToast('Bridge deleted.');
      setDeleteTarget(null);
      loadBridges();
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setDeleting(false);
    }
  };

  const assignedBridges = useMemo(
    () => filterBridgesByRole(bridges, user),
    [bridges, user]
  );

  const mapBridges = useMemo(
    () => filterBridgesForMap(bridges, user),
    [bridges, user]
  );

  const assignedBridgeIds = useMemo(
    () => assignedBridges.map((b) => b._id),
    [assignedBridges]
  );

  const filtered = assignedBridges.filter((b) =>
    [b.name, b.country, b.state, b.city, b.division]
      .join(' ')
      .toLowerCase()
      .includes(search.toLowerCase())
  );
  const totalPages = Math.max(1, Math.ceil(filtered.length / BRIDGE_LIST_PAGE_SIZE) || 1);
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice(
    (currentPage - 1) * BRIDGE_LIST_PAGE_SIZE,
    currentPage * BRIDGE_LIST_PAGE_SIZE,
  );

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const bridgeStats = useMemo(() => {
    const countries = new Set(assignedBridges.map((b) => b.country).filter(Boolean));
    return {
      total: assignedBridges.length,
      countries: countries.size,
    };
  }, [assignedBridges]);

  const tableColSpan = canEditDelete ? 10 : 8;

  const modalTitle =
    modalStep === 1
      ? modal === 'add'
        ? 'Add New Bridge'
        : 'Edit Bridge'
      : 'Bridge';

  const handleAssignedBridgeMapClick = useCallback(
    (bridge) => {
      navigate('/sensor-data', { state: { bridgeId: bridge._id } });
    },
    [navigate]
  );

  const handleMapSensorSerialClick = useCallback(
    ({ bridgeId, sensorId, sensorSerialNo }) => {
      if (!bridgeId) return;
      const bridge = bridges.find((item) => String(item._id) === String(bridgeId));
      const sensor = (bridge?.sensors || []).find((item) => (
        (sensorId && String(item._id) === String(sensorId))
        || (sensorSerialNo && String(item.sensorSerialNo) === String(sensorSerialNo))
      ));
      const rawLoggerId = sensor?.dataLoggerId;
      const dataLoggerId = rawLoggerId && typeof rawLoggerId === 'object'
        ? (rawLoggerId._id || '')
        : (rawLoggerId || '');
      const dataLoggerSerialNo = sensor?.dataLoggerSerialNo
        || (rawLoggerId && typeof rawLoggerId === 'object' ? (rawLoggerId.serialNo || '') : '')
        || (bridge?.dataLoggers || []).find((dl) => String(dl._id) === String(dataLoggerId))?.serialNo
        || '';
      navigate('/sensor-data', {
        state: {
          bridgeId,
          sensorId: sensorId || sensor?._id || '',
          sensorSerialNo: sensorSerialNo || sensor?.sensorSerialNo || '',
          sensorType: sensor?.sensorType || '',
          dataLoggerId,
          dataLoggerSerialNo,
        },
      });
    },
    [navigate, bridges]
  );

  const handleBridgeNameClick = (bridge) => {
    if (!getBridgeCoordinates(bridge)) {
      showToast('No coordinates saved for this bridge.', 'error');
      return;
    }
    setMapBridge(bridge);
    setBlinkingRowId(bridge._id);
    setPulseBridgeId(bridge._id);
    setMapPulseToken((token) => token + 1);
    setListView('map');
  };

  const handleBridgeRowBlinkEnd = (bridgeId, event) => {
    if (event.animationName !== 'bridge-list-row-blink') return;
    setBlinkingRowId((current) => (current === bridgeId ? null : current));
  };

  const headerCoords = mapBridge ? getBridgeCoordinates(mapBridge) : null;
  const bridgesOnMapCount = useMemo(
    () => mapBridges.filter((b) => getBridgeCoordinates(b)).length,
    [mapBridges]
  );

  const viewTabs = (
    <div className="bridge-list-view-tabs" role="tablist" aria-label="Bridge view">
      <button
        type="button"
        role="tab"
        aria-selected={listView === 'map'}
        className={`bridge-list-view-tab${listView === 'map' ? ' is-active' : ''}`}
        onClick={() => setListView('map')}
      >
        <i className="ti ti-map-2" />
        Map
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={listView === 'list'}
        className={`bridge-list-view-tab${listView === 'list' ? ' is-active' : ''}`}
        onClick={() => setListView('list')}
      >
        <i className="ti ti-list" />
        List
      </button>
    </div>
  );

  return (
    <div className="bridge-list-page">
      {canAdd && user?.role !== 'super_admin' && (
        <button type="button" className="bridge-list-add-btn" onClick={openAdd}>
          <i className="ti ti-plus" /> Add a new bridge
        </button>
      )}

      <div className="bridge-list-workspace">
        <div className="bridge-list-workspace-header">
          <div className="bridge-map-panel-title">
            <i className="ti ti-map-pin" />
            <div>
              <strong>{mapBridge ? mapBridge.name : 'All Bridges'}</strong>
              {headerCoords ? (
                <span>
                  {headerCoords.lat}, {headerCoords.lng}
                </span>
              ) : (
                <span>{bridgesOnMapCount} bridge{bridgesOnMapCount === 1 ? '' : 's'} on map</span>
              )}
            </div>
          </div>
          {viewTabs}
        </div>

        <div className={`bridge-list-map-body${listView === 'map' ? '' : ' is-hidden'}`}>
          <BridgeMapPanel
            hideHeader
            bridges={mapBridges}
            assignedBridgeIds={assignedBridgeIds}
            selectedBridge={mapBridge}
            pulseBridgeId={pulseBridgeId}
            pulseToken={mapPulseToken}
            onAssignedBridgeClick={handleAssignedBridgeMapClick}
            onSensorSerialClick={handleMapSensorSerialClick}
            currentUser={user}
            onLoadError={(message) => showToast(message, 'error')}
          />
        </div>

        {listView === 'list' && (
        <div className="bridge-list-table-card">
          <div className="bridge-list-table-header">
            <div className="bridge-list-table-title-block">
              <div className="bridge-list-table-title">
                <i className="ti ti-arrows-exchange" />
                Added bridges
              </div>
              <div className="bridge-list-table-subtitle">
                {bridgeStats.total} bridge{bridgeStats.total === 1 ? '' : 's'} across{' '}
                {bridgeStats.countries} {bridgeStats.countries === 1 ? 'country' : 'countries'}
              </div>
            </div>
            <div className="bridge-list-search">
              <i className="ti ti-search" />
              <input
                type="text"
                placeholder="Search bridges..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>
          </div>

          {loading ? (
            <Loading />
          ) : (
            <table className="bridge-list-table">
              <thead>
                <tr>
                  <th className="col-index">Sr.no.</th>
                  <th className="col-bridge">Bridge</th>
                  <th>Country</th>
                  <th>State</th>
                  <th>City</th>
                  <th>Division</th>
                  <th>Zip Code</th>
                  <th>Sensors</th>
                  {canEditDelete && (
                    <>
                      <th>Edit</th>
                      <th>Delete</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={tableColSpan} className="bridge-list-empty">
                      No bridges found.
                    </td>
                  </tr>
                ) : (
                  pageItems.map((b, idx) => (
                    <tr
                      key={b._id}
                      className={[
                        mapBridge?._id === b._id ? 'bridge-list-row--selected' : '',
                        blinkingRowId === b._id ? 'bridge-list-row--blink' : '',
                      ]
                        .filter(Boolean)
                        .join(' ') || undefined}
                      onAnimationEnd={(event) => handleBridgeRowBlinkEnd(b._id, event)}
                    >
                      <td className="col-index">
                        {String((currentPage - 1) * BRIDGE_LIST_PAGE_SIZE + idx + 1).padStart(2, '0')}
                      </td>
                      <td className="col-bridge">
                        <div className="bridge-row-identity">
                          <div
                            className="bridge-avatar"
                            aria-hidden="true"
                          >
                            <i className="ti ti-arrows-exchange" />
                          </div>
                          <button
                            type="button"
                            className={`bridge-name-link${mapBridge?._id === b._id ? ' bridge-name-link--active' : ''}`}
                            onClick={() => handleBridgeNameClick(b)}
                            title="Show bridge location on map"
                          >
                            {b.name}
                          </button>
                        </div>
                      </td>
                      <td>{b.country}</td>
                      <td>{b.state}</td>
                      <td>{b.city}</td>
                      <td>{b.division}</td>
                      <td>{b.zipCode}</td>
                      <td>
                        <span className="bridge-sensor-badge">
                          {b.sensors?.length || 0} sensors
                        </span>
                      </td>
                      {canEditDelete && (
                        <>
                          <td>
                            <button className="btn btn-blue btn-sm" onClick={() => openEdit(b)}>
                              Edit
                            </button>
                          </td>
                          <td>
                            <button
                              className="btn btn-red btn-sm"
                              onClick={() => setDeleteTarget({ id: b._id, name: b.name })}
                              title="Delete bridge"
                            >
                              <i className="ti ti-trash" />
                            </button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
          {!loading && filtered.length > BRIDGE_LIST_PAGE_SIZE && (
            <div className="bridge-list-pagination">
              <span>
                Showing {(currentPage - 1) * BRIDGE_LIST_PAGE_SIZE + 1}
                –
                {Math.min(currentPage * BRIDGE_LIST_PAGE_SIZE, filtered.length)}
                {' '}of {filtered.length}
              </span>
              <div className="bridge-list-pagination-actions">
                <button
                  type="button"
                  className="bridge-list-pagination-btn"
                  disabled={currentPage <= 1}
                  onClick={() => setPage(currentPage - 1)}
                >
                  Previous
                </button>
                <span className="bridge-list-pagination-info">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  type="button"
                  className="bridge-list-pagination-btn"
                  disabled={currentPage >= totalPages}
                  onClick={() => setPage(currentPage + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
        )}
      </div>

      {deleteTarget && (
        <ConfirmModal
          title="Delete Bridge"
          message={
            <>
              Delete <strong>{deleteTarget.name}</strong>? This cannot be undone.
            </>
          }
          confirmLabel="Delete"
          cancelLabel="Cancel"
          variant="danger"
          loading={deleting}
          onConfirm={confirmDeleteBridge}
          onCancel={() => !deleting && setDeleteTarget(null)}
        />
      )}

      {modal && (
        <Modal
          title={modalTitle}
          onClose={closeModal}
          width={modalStep === 2 ? 820 : 680}
          className={modalStep === 1 ? 'modal--bridge-form' : ''}
        >
          {modalStep === 1 ? (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label>Select Country</label>
                  <select value={form.country} onChange={(e) => onCountryChange(e.target.value)}>
                    {COUNTRIES.map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Select State</label>
                  <AppMultiSelect
                    values={form.states}
                    onChange={(states) => setForm((p) => ({ ...p, states }))}
                    options={stateOptions.map((s) => ({ value: s, label: s }))}
                    placeholder="Select State"
                    checkboxPosition="right"
                    aria-label="Select State"
                  />
                </div>
                <div className="form-group">
                  <label>City</label>
                  <input type="text" value={form.city} onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Division</label>
                  <input
                    type="text"
                    value={form.division}
                    onChange={(e) => setForm((p) => ({ ...p, division: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>Bridge Coordinates</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      type="number"
                      step="any"
                      placeholder="Lat"
                      value={form.lat}
                      onChange={(e) => setForm((p) => ({ ...p, lat: e.target.value }))}
                    />
                    <input
                      type="number"
                      step="any"
                      placeholder="Lng"
                      value={form.lng}
                      onChange={(e) => setForm((p) => ({ ...p, lng: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Zip Code</label>
                  <input
                    type="text"
                    value={form.zipCode}
                    onChange={(e) => setForm((p) => ({ ...p, zipCode: e.target.value }))}
                  />
                </div>
              </div>
              <div className="form-row two">
                <div className="form-group">
                  <label>Bridge Name *</label>
                  <input type="text" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
                </div>
              </div>
              <p className="step-hint">After saving, you can add Bridge Admin, Manager, Owner, and Engineers.</p>
              <LoadingButton
                className="btn btn-pink"
                style={{ width: '100%', padding: 10, fontSize: 14 }}
                onClick={handleStep1Save}
                loading={saving}
                loadingLabel="Saving..."
              >
                Save & Continue
              </LoadingButton>
            </>
          ) : (
            <>
              <div className="bridge-step-summary">
                <div className="bridge-step-summary-text">
                  <strong>{form.name}</strong>
                  <span>
                    {form.city}
                    {form.states?.length ? `, ${formatStates(form.states)}` : ''}, {form.country}
                  </span>
                </div>
              </div>
              <BridgeStaffForm
                bridgeCountry={form.country}
                companyCountry={companyCountry}
                emptyRowDial={companyCountry ? getDialCodeForBridgeCountry(companyCountry) : ''}
                owners={staff.owners}
                admins={staff.admins}
                managers={staff.managers}
                onOwnersChange={(owners) => setStaff((s) => ({ ...s, owners }))}
                onAdminsChange={(admins) => setStaff((s) => ({ ...s, admins }))}
                onManagersChange={(managers) => setStaff((s) => ({ ...s, managers }))}
              />
              <BridgeSensorsReadonly sensors={sensors} dataLoggers={dataLoggers} />
              <div className="bridge-modal-actions">
                <button
                  type="button"
                  className="btn bridge-step-back"
                  onClick={() => setModalStep(1)}
                >
                  Back
                </button>
                <LoadingButton
                  className="btn btn-teal"
                  onClick={handleStep2SaveClose}
                  loading={saving}
                  loadingLabel="Saving..."
                >
                  Save & Close
                </LoadingButton>
              </div>
            </>
          )}
        </Modal>
      )}
    </div>
  );
}
